import React from 'react'

import { Box, Chip, Typography } from '@mui/material'
import {
  IconCalendar,
  IconDatabase,
  IconLink,
  IconSparkles,
} from '@tabler/icons-react'
import dayjs from 'dayjs'

import ObjectChip from 'src/components/Chips/ObjectChip/ObjectChip'
import Row from 'src/components/Row/Row'
import { ObjectTypeMetadata } from 'src/lib/objects'

const containerSx = {
  px: '12px',
  gap: 2,
  alignItems: 'flex-start',
  '& .thread-message-tool-icon': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px !important',
    height: '48px !important',
    backgroundColor: (theme) => theme.palette.divider,
    borderRadius: '8px',
    flexShrink: 0,
    mt: 0.5,
  },

  '& .thread-message-tool-content': {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    width: '100%',
  },

  '& .thread-message-tool-label-row': {
    alignItems: 'center',
    gap: 0.5,
    mb: 1,
  },

  '& .thread-message-tool-label': {
    fontWeight: 600,
  },

  '& .thread-message-context-plan-reasoning-text': {
    mb: 1.5,
  },

  '& .thread-message-context-plan-metadata-section': {
    display: 'flex',
    gap: 2,
    pl: 1,
    borderLeft: (theme) => `2px solid ${theme.palette.divider}`,
  },

  '& .thread-message-context-plan-metadata-content': {
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
  },

  '& .thread-message-context-plan-metadata-item': {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1,
  },

  '& .thread-message-context-plan-metadata-icon': {
    mt: '2px',
    color: (theme) => theme.palette.text.secondary,
  },

  '& .thread-message-context-plan-metadata-text-content': {
    display: 'flex',
    flexDirection: 'row',
    gap: 0.5,
  },

  '& .thread-message-context-plan-metadata-label': {
    variant: 'body2',
    fontWeight: 500,
    color: (theme) => theme.palette.text.secondary,
  },

  '& .thread-message-context-plan-values-row': {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0.5,
  },

  '& .thread-message-context-plan-timeframe-text': {
    variant: 'body2',
  },
}

const ThreadContextPlan = ({
  plan,
  threadMessageId,
}: {
  plan: any
  threadMessageId: string
}) => {
  return (
    <Row sx={containerSx}>
      <Box className="thread-message-tool-icon">
        <IconSparkles size={26} />
      </Box>
      <Box className="thread-message-tool-content">
        <Row className="thread-message-tool-label-row">
          <IconSparkles size={12} />
          <Typography className="thread-message-tool-label">
            Searching for relevant objects in your CRM to answer your question
          </Typography>
        </Row>
        <Typography className="thread-message-context-plan-reasoning-text">
          {plan.reasoning}
        </Typography>
        <Box className="thread-message-context-plan-metadata-section">
          <Box className="thread-message-context-plan-metadata-content">
            {plan.objectTypesToSuggest &&
              plan.objectTypesToSuggest.length > 0 && (
                <Box className="thread-message-context-plan-metadata-item">
                  <IconDatabase
                    size={16}
                    className="thread-message-context-plan-metadata-icon"
                  />
                  <Box className="thread-message-context-plan-metadata-text-content">
                    <Typography className="thread-message-context-plan-metadata-label">
                      Searching for object types:
                    </Typography>
                    <Row className="thread-message-context-plan-values-row">
                      {plan.objectTypesToSuggest.map((objectType) => (
                        <Chip
                          key={`object-type-${objectType}-${threadMessageId}`}
                          icon={React.createElement(
                            ObjectTypeMetadata[objectType]?.icon || IconSparkles
                          )}
                          label={
                            ObjectTypeMetadata[objectType]?.label || objectType
                          }
                          size="small"
                        />
                      ))}
                    </Row>
                  </Box>
                </Box>
              )}

            {plan.timeframeStart && plan.timeframeEnd && (
              <Box className="thread-message-context-plan-metadata-item">
                <IconCalendar
                  size={16}
                  className="thread-message-context-plan-metadata-icon"
                />
                <Box className="thread-message-context-plan-metadata-text-content">
                  <Typography className="thread-message-context-plan-metadata-label">
                    Within timeframe:
                  </Typography>
                  <Typography className="thread-message-context-plan-timeframe-text">
                    {dayjs(plan.timeframeStart).format('MMM D, YYYY')} -{' '}
                    {dayjs(plan.timeframeEnd).format('MMM D, YYYY')}
                  </Typography>
                </Box>
              </Box>
            )}

            {plan.clientProvidedObjects &&
              plan.clientProvidedObjects.length > 0 && (
                <Box className="thread-message-context-plan-metadata-item">
                  <IconLink
                    size={16}
                    className="thread-message-context-plan-metadata-icon"
                  />
                  <Box className="thread-message-context-plan-metadata-text-content">
                    <Typography className="thread-message-context-plan-metadata-label">
                      Related to objects:
                    </Typography>
                    <Row className="thread-message-context-plan-values-row">
                      {plan.clientProvidedObjects.map((object) => (
                        <ObjectChip
                          key={`object-${object.objectId}-${threadMessageId}`}
                          workspaceId={plan.workspaceId}
                          crmObject={object}
                          showSidebar={true}
                        />
                      ))}
                    </Row>
                  </Box>
                </Box>
              )}
          </Box>
        </Box>
      </Box>
    </Row>
  )
}

export default ThreadContextPlan
